import { useCallback, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import { Organization } from 'dashboard/models/organization';
import { WorkflowStepInput, WorkflowVersion } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setInputStep, setWorkflowVersion } from 'dashboard/redux/actions/workflow';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import { clearAccountAction } from 'dashboard/redux/slices/account-action';
import { RootState } from 'dashboard/redux/store';
import {
  activateAccountAction,
  archiveAccountAction,
  deactivateAccountAction,
  loadAccountAction,
  restoreAccountActionDraft,
  updateAccountActionName,
} from 'dashboard/redux/thunk-actions/account-action';
import {
  discardDraftWorkflowVersion,
  loadWorkflowVersion,
  rolloutWorkflowVersion,
  saveWorkflowVersion,
  validateWorkflow,
} from 'dashboard/redux/thunk-actions/workflow';
import {
  fetchObjectJsonSchema,
  loadWorkflowSchemaParallel,
} from 'dashboard/redux/thunk-actions/workflow-schema';
import { loadWorkflowVersionList } from 'dashboard/redux/thunk-actions/workflow-versions';
import DashboardAccountActionsShowView from 'dashboard/views/DashboardAccountActionsShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

interface DashboardAccountActionsShowRouteProps {
  organization: Organization;
}

type Params = {
  accountTypeId: string;
  accountActionId: string;
  accountActionWorkflowVersionId: string;
};

const DashboardAccountActionsShowRoute = (
  props: DashboardAccountActionsShowRouteProps & PropsFromRedux,
) => {
  useOnMountAndUnmount(() => {
    props.clearAccountAction();
  });
  const { inputStep, setWorkflowVersion, setInputStep } = props;
  const { accountTypeId, accountActionId, accountActionWorkflowVersionId } =
    useParams<Params>() as Params;

  const { trigger } = inputStep || {};
  const navigate = useNavigate();
  const [triggerIsDirty, setTriggerIsDirty] = useState(false);
  const [versionIsDirty, setVersionIsDirty] = useState(false);

  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();
  const [debouncedValidateWorkflow] = useDebounced(
    (vendors: ExternalIntegrationVendor[]) => props.validateWorkflow(vendors),
    333,
  );

  const isDirty = triggerIsDirty || versionIsDirty;

  const { loading: loadingAccountAction, callback: loadAccountAction } = useApiLoader({
    loader: async () => {
      return Promise.all([
        props.loadAccountAction(accountActionId),
        props.loadWorkflowSchemaParallel(),
      ]).then(() => {
        return props.loadWorkflowVersion(accountActionWorkflowVersionId);
      });
    },
    deps: [
      props.loadAccountAction,
      props.loadWorkflowVersion,
      props.loadWorkflowSchemaParallel,
      accountActionId,
      accountActionWorkflowVersionId,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: archiveAccountActionError, callback: handleArchiveAccountAction } =
    useApiCallback(async () => {
      await props.archiveAccountAction(accountActionId);
      await loadAccountAction();
      props.showMessage('Successfully archived account action.', MessageType.Success);
    }, [accountActionId, props.archiveAccountAction, loadAccountAction]);

  useApiErrorHandler({
    error: archiveAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: activateAccountActionError, callback: handleActivateAccountAction } =
    useApiCallback(async () => {
      await props.activateAccountAction(accountActionId);
      props.showMessage('Successfully activated account action.', MessageType.Success);
    }, [accountActionId, props.activateAccountAction]);

  useApiErrorHandler({
    error: activateAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to activate account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: deactivateAccountActionError, callback: handleDeactivateAccountAction } =
    useApiCallback(async () => {
      await props.deactivateAccountAction(accountActionId);
      props.showMessage('Successfully deactivated account action.', MessageType.Success);
    }, [accountActionId, props.deactivateAccountAction]);

  useApiErrorHandler({
    error: deactivateAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to deactivate account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: saveAccountActionError, callback: handleSaveAccountAction } =
    useApiCallback(async () => {
      // We set state before saving because saving will cause redirects triggering the unsaved changes prompt
      // FIXME: Need to figure out how to persist dirty state when saving fails
      setTriggerIsDirty(false);
      setVersionIsDirty(false);
      if (trigger?.type !== ApiModel.TriggerAccount) {
        return;
      }

      const updatedVersionId = await props.saveWorkflowVersion(
        props.actionWorkflow.id,
        props.actionWorkflowVersion,
        trigger,
        {
          'trigger-type': trigger.type,
          'account-type-id': accountTypeId,
        },
      );

      if (updatedVersionId) {
        const toLink = `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/${updatedVersionId}`;
        navigate(toLink, { replace: true });
        props.showMessage('Successfully saved account action as draft.', MessageType.Success);
      }
    }, [
      accountTypeId,
      accountActionId,
      props.actionWorkflow?.id,
      props.actionWorkflowVersion,
      trigger,
      props.saveWorkflowVersion,
      props.archiveAccountAction,
      setTriggerIsDirty,
      setVersionIsDirty,
    ]);

  useApiErrorHandler({
    error: saveAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: publishAccountActionError, callback: handlePublishAccountAction } =
    useApiCallback(async () => {
      setTriggerIsDirty(false);
      setVersionIsDirty(false);
      if (trigger?.type !== ApiModel.TriggerAccount) {
        return;
      }

      const nextPublishedVersionId = await props.rolloutWorkflowVersion(
        props.actionWorkflow,
        props.actionWorkflowVersion,
        trigger,
        100,
        isDirty,
        props.environmentIds,
        {
          meta: {
            'trigger-type': trigger.type,
            'account-type-id': accountTypeId,
          },
        },
      );

      if (nextPublishedVersionId) {
        const toLink = `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/${nextPublishedVersionId}`;
        navigate(toLink, { replace: true });
        props.showMessage('Successfully published account action.', MessageType.Success);
      }
    }, [
      props.rolloutWorkflowVersion,
      props.showMessage,
      accountTypeId,
      props.actionWorkflow,
      props.actionWorkflowVersion,
      trigger,
      props.environmentIds,
      versionIsDirty,
      triggerIsDirty,
    ]);

  useApiErrorHandler({
    error: publishAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to publish account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: handleUpdateAccountActionNameError, callback: handleUpdateAccountActionName } =
    useApiCallback(
      async (name: string) => {
        await props.updateAccountActionName(name, accountActionId);
      },
      [accountActionId, props.updateAccountActionName],
    );

  const { callback: loadWorkflowVersion } = useApiCallback(
    async (workflowVersionId: string) => {
      await props.loadWorkflowVersion(workflowVersionId);
      navigate(
        `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/${workflowVersionId}`,
        { replace: true },
      );

      setTriggerIsDirty(false);
      setVersionIsDirty(false);
    },
    [props.loadWorkflowVersion],
  );

  useApiErrorHandler({
    error: handleUpdateAccountActionNameError,
    onError: () =>
      props.showMessage('Failed to update account action.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const updateActionWorkflowVersion = useCallback(
    (workflowVersion: WorkflowVersion) => {
      setWorkflowVersion(workflowVersion);
      setVersionIsDirty(true);
      debouncedValidateWorkflow(externalIntegrationVendors);
    },
    [setWorkflowVersion, debouncedValidateWorkflow, externalIntegrationVendors],
  );
  const updateInputStep = useCallback(
    (inputStep: WorkflowStepInput) => {
      setInputStep(inputStep);
      setTriggerIsDirty(true);
      debouncedValidateWorkflow(externalIntegrationVendors);
    },
    [setInputStep, debouncedValidateWorkflow, externalIntegrationVendors],
  );

  const {
    callback: loadVersionList,
    error: loadVersionListError,
    loading: isLoadingVersionList,
  } = useApiCallback(
    () =>
      props.loadWorkflowVersionList({
        workflowId: props.actionWorkflow.id,
        triggerType: trigger?.type,
        currentPage: props.workflowVersionsCurrentPage,
        pageSize: props.workflowVersionsPageSize,
      }),
    [
      props.loadWorkflowVersionList,
      props.workflowVersionsCurrentPage,
      props.workflowVersionsPageSize,
      props.actionWorkflow?.id,
    ],
  );
  useApiErrorHandler({
    error: loadVersionListError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load Version History.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: restoreDraftVersion } = useApiCallback(async () => {
    if (accountTypeId && accountActionId) {
      const { actionWorkflow } = await props.restoreAccountActionDraft(
        accountActionId,
        accountActionWorkflowVersionId,
      );

      navigate(
        `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/${actionWorkflow?.relationships?.['latest-draft-version']?.data?.id}`,
        { replace: true },
      );
    } else {
      props.showMessage('Failed to determine account type or action.', MessageType.Failure);
    }

    await loadVersionList();
    props.showMessage('Successfully restored draft version.', MessageType.Success);
  }, [
    accountTypeId,
    accountActionId,
    accountActionWorkflowVersionId,
    props.restoreAccountActionDraft,
    props.showMessage,
    loadVersionList,
  ]);

  const { callback: handleDiscardDraft } = useApiCallback(async () => {
    if (!accountActionWorkflowVersionId) {
      return;
    }

    const result = await props.discardDraftWorkflowVersion(
      props.actionWorkflow,
      accountActionWorkflowVersionId,
    );

    if (result) {
      navigate(result.redirectPath);
    }
  }, [accountActionWorkflowVersionId, props.discardDraftWorkflowVersion]);

  if (
    props.accountAction == null ||
    props.actionWorkflow == null ||
    props.workflowSchema == null ||
    props.actionWorkflowVersion == null ||
    inputStep == null
  ) {
    return loadingAccountAction ? (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message='We couldn’t find that Account Action.'
          backRoute={`/dashboard/account-types/${accountTypeId}/actions`}
        />
      </Backdrop>
    );
  }

  return (
    <DashboardAccountActionsShowView
      isLoading={loadingAccountAction}
      accountTypeId={accountTypeId}
      accountAction={props.accountAction}
      actionWorkflow={props.actionWorkflow}
      actionWorkflowVersion={props.actionWorkflowVersion}
      editingWorkflowVersionId={accountActionWorkflowVersionId}
      actionWorkflowDeployment={props.actionWorkflowDeployment}
      validationErrors={props.actionWorkflowVersionErrors}
      inputStep={inputStep}
      versionsList={props.versionsList}
      workflowSchema={props.workflowSchema}
      loadActionWorkflowVersion={loadWorkflowVersion}
      handleArchiveAccountAction={handleArchiveAccountAction}
      handleUpdateAccountActionName={handleUpdateAccountActionName}
      updateActionWorkflowVersion={updateActionWorkflowVersion}
      isDirty={isDirty}
      handleSaveAccountAction={handleSaveAccountAction}
      handlePublishAccountAction={handlePublishAccountAction}
      handleActivateAccountAction={handleActivateAccountAction}
      handleDeactivateAccountAction={handleDeactivateAccountAction}
      updateInputStep={updateInputStep}
      showMessage={props.showMessage}
      isLoadingVersionList={isLoadingVersionList}
      loadVersionList={loadVersionList}
      restoreDraftVersion={restoreDraftVersion}
      handleDiscardDraft={handleDiscardDraft}
      fetchPlatformObjectJsonSchema={props.fetchObjectJsonSchema}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountAction: state.accountAction.accountAction,
    actionWorkflow: state.workflow.present.workflow,
    actionWorkflowVersion: state.workflow.present.workflowVersion,
    actionWorkflowDeployment: state.workflow.present.workflowDeployment,
    actionWorkflowVersionErrors: state.workflow.present.workflowVersionErrors,
    inputStep: state.workflow.present.inputStep,
    workflowSchema: state.workflowSchema,
    environmentIds: state.organization.environments.map((e) => e.id),
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.accounts.currentPage,
    workflowVersionsCurrentPage: state.workflowVersions.currentPage,
    workflowVersionsPageSize: state.workflowVersions.pageSize,
    versionsList: state.workflowVersions.workflowVersions,
  };
};

const mapDispatchToProps = {
  archiveAccountAction,
  loadAccountAction,
  loadWorkflowVersionList,
  loadWorkflowSchemaParallel,
  saveWorkflowVersion,
  activateAccountAction,
  deactivateAccountAction,
  rolloutWorkflowVersion,
  updateAccountActionName,
  loadWorkflowVersion,
  validateWorkflow,
  clearAccountAction,
  setWorkflowVersion,
  setInputStep,
  showMessage,
  restoreAccountActionDraft,
  discardDraftWorkflowVersion,
  fetchObjectJsonSchema,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardAccountActionsShowRoute);
